import { forwardRef } from 'react';

import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { TypographyOwnProps } from '@mui/material/Typography';

import { styled } from '../../styles';
import { ListItemIcon, ListItemText } from '../list';
import { MenuItem, type MenuItemProps } from '../menu-item';

interface DropdownMenuItemOwnProps
  extends Omit<
    MenuItemProps,
    'children' | 'color' | 'LinkComponent' | 'divider'
  > {
  icon?: IconDefinition;
  label: string;
  /**
   * The color of the text and icon if provided.
   * @default undefined
   */
  color?: TypographyOwnProps['color'];
}

export type DropdownMenuItemProps<Component extends React.ElementType = 'li'> =
  {
    component?: Component;
  } & Omit<
    React.ComponentPropsWithoutRef<Component>,
    keyof DropdownMenuItemOwnProps
  > &
    DropdownMenuItemOwnProps;

type DropdownMenuItemRef<Component extends React.ElementType> =
  Component extends 'li'
    ? React.ComponentRef<'li'>
    : React.ComponentRef<Component>;

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  variants: [
    {
      props: ({ color }) => color !== undefined,
      style: {
        '& .MuiListItemIcon-root': {
          color: 'inherit',
        },
      },
    },
    {
      props: { color: 'primary' },
      style: {
        color: theme.palette.primary.main,
      },
    },
    {
      props: { color: 'secondary' },
      style: {
        color: theme.palette.secondary.main,
      },
    },
    {
      props: { color: 'success' },
      style: {
        color: theme.palette.success.main,
      },
    },
    {
      props: { color: 'error' },
      style: {
        color: theme.palette.error.main,
      },
    },
    {
      props: { color: 'info' },
      style: {
        color: theme.palette.info.main,
      },
    },
    {
      props: { color: 'warning' },
      style: {
        color: theme.palette.warning.main,
      },
    },
    {
      props: { color: 'textPrimary' },
      style: {
        color: theme.palette.text.primary,
      },
    },
    {
      props: { color: 'textSecondary' },
      style: {
        color: theme.palette.text.secondary,
      },
    },
    {
      props: { color: 'textDisabled' },
      style: {
        color: theme.palette.text.disabled,
      },
    },
  ],
})) as typeof MenuItem;

// need to use type assertion to restores the original function signature
// https://fettblog.eu/typescript-react-generic-forward-refs/
// In React 19, forwardRef is no longer necessary so this will be removed once we update
export const DropdownMenuItem = forwardRef(
  <Component extends React.ElementType = 'li'>(
    { icon, label, component, ...rest }: DropdownMenuItemProps<Component>,
    ref: React.ForwardedRef<DropdownMenuItemRef<Component>>,
  ) => {
    // According to HTML specifications, the <ul> element should only contain <li> elements as its direct children.
    const wrapComponent = component && component !== 'li';

    const menuItem = (
      // TODO: S2CX-18589: Add tooltip conditionally to handle limitation
      // https://mui.com/material-ui/react-menu/#limitations
      <StyledMenuItem ref={ref} component={component} {...rest}>
        {icon && (
          <ListItemIcon>
            <FontAwesomeIcon icon={icon} />
          </ListItemIcon>
        )}

        <ListItemText
          slotProps={{
            primary: {
              noWrap: true,
            },
          }}
        >
          {label}
        </ListItemText>
      </StyledMenuItem>
    );

    return wrapComponent ? <li role="presentation">{menuItem}</li> : menuItem;
  },
) as <Component extends React.ElementType = 'li'>(
  props: DropdownMenuItemProps<Component> & {
    ref?: React.ForwardedRef<DropdownMenuItemRef<Component>>;
  },
) => React.ReactElement;
